import { Input, useDisclosure, useMediaQuery } from '@chakra-ui/react';
import { getChildrenDeep } from '@diamond/shared/utils';
import { useRouteQuery } from '@diamond/sol-buyer/utils';
import { useRouter } from 'next/router';
import { ComponentProps, ReactNode, RefObject, useRef, useState } from 'react';

export type AutocompleteProps = {
  name?: string;
  keyword?: string;
  setKeyword: (keyword: string) => void;
  defaultIsOpen?: boolean;
  isLoading?: boolean;
  emptyResult?: boolean;
  children: ReactNode;
  onSelect: (keyword: string) => void;
  openMode?: 'typing' | 'focus';
};

type ChakraInputProps = ComponentProps<typeof Input>;

export type UseAutocompleteReturn = {
  keyword?: string;
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
  isLoading: boolean;
  emptyResult: boolean;
  focusedValue: string;
  items: string[];
  getInputProps: (props: ChakraInputProps) => ChakraInputProps;
  onSelect: (keyword: string) => void;
  clearKeyword: () => void;
  inputRef: RefObject<HTMLInputElement>;
  listRef: RefObject<HTMLElement>;
};

export function useAutocomplete(
  props: AutocompleteProps
): UseAutocompleteReturn {
  const {
    defaultIsOpen,
    isLoading = false,
    emptyResult = false,
    openMode = 'typing',
    keyword,
    setKeyword,
  } = props;
  const { isOpen, onClose, onOpen } = useDisclosure({ defaultIsOpen });

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const [isMobile] = useMediaQuery('(max-width: 768px)');

  const router = useRouter();
  const params = useRouteQuery();
  const inBrowseProduct =
    router.pathname.includes('product') && Boolean(params['keyword']);
  const inRecipe = router.pathname.includes('recipe') && Boolean(params['q']);

  const items = getItems<string>(props.children, (item) => item.props['text']);
  const [focusedValue, setFocusedValue] = useState<string>(items[0] || '');

  const focusedIndex = items.findIndex((i) => i === focusedValue);

  const prevItem = moveItem(focusedIndex, items, (i) => i - 1);
  const nextItem = moveItem(focusedIndex, items, (i) => i + 1);

  const reset = (keyword: string) => {
    setFocusedValue(keyword);
    setKeyword(keyword);
    onClose();
  };

  const onSelect = (keyword: string) => {
    props.onSelect(keyword);
    reset(keyword);
  };

  const clearKeyword = () => {
    inputRef.current?.focus();
    if (inBrowseProduct || inRecipe) {
      props.onSelect('');
      reset('');
      return;
    }
    setKeyword('');
    onClose();
  };

  const getInputProps: UseAutocompleteReturn['getInputProps'] = (props) => {
    const { onChange } = props;
    return {
      ...props,
      onFocus(e) {
        if (keyword || openMode === 'focus') {
          onOpen();
          !isMobile && e.target.select();
        }
      },
      onClick() {
        setFocusedValue('');
      },
      onBlur(e) {
        // Focus management detect blur outside autocomplete
        // https://stackoverflow.com/questions/121499/when-a-blur-event-occurs-how-can-i-find-out-which-element-focus-went-to
        const isListFocused =
          e.relatedTarget === listRef?.current ||
          listRef.current?.contains(e.relatedTarget);

        if (!isListFocused) {
          onClose();
          setFocusedValue('');
        }
      },
      onChange(e) {
        e.persist();
        if (typeof onChange === 'function') onChange(e);
        const newValue = e.target.value;
        setKeyword(newValue);
        if (newValue) {
          onOpen();
        } else {
          onClose();
        }
      },
      onKeyDown(e) {
        const { key } = e;
        if (key === 'Enter') {
          const focused = items[focusedIndex];
          if (focused) {
            onSelect(focused);
          } else {
            onSelect(e.currentTarget.value);
          }
          e.preventDefault();
          return;
        }

        if (key === 'ArrowDown') {
          if (!isOpen && keyword !== '') onOpen();
          else setFocusedValue(nextItem);
          e.preventDefault();
          return;
        }

        if (key === 'ArrowUp') {
          if (!isOpen && keyword !== '') onOpen();
          else setFocusedValue(prevItem);
          e.preventDefault();
          return;
        }

        if (key === 'Escape') {
          onClose();
          setFocusedValue('');
          e.preventDefault();
          return;
        }
      },
    };
  };

  return {
    keyword,
    isOpen,
    onClose,
    onOpen,
    isLoading,
    emptyResult,
    focusedValue,
    items,
    getInputProps,
    onSelect,
    clearKeyword,
    inputRef,
    listRef,
  };
}

function getItems<TReturn = unknown>(
  children: ReactNode,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  predicate: (item: any) => TReturn
) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const items = getChildrenDeep(children, (item: any) => {
    return item?.type?.displayName === 'AutocompleteItem';
  });
  return items.map(predicate) as TReturn[];
}

function moveItem<TItems extends string[]>(
  currentIdx: number,
  items: TItems,
  strategy: (currentIdx: number) => number
): string {
  if (currentIdx === -1) return items[0];
  return items[wrap(0, items.length, strategy(currentIdx))];
}

function wrap(min: number, max: number, v: number) {
  const rangeSize = max - min;
  return ((((v - min) % rangeSize) + rangeSize) % rangeSize) + min;
}
